import { API_MODE, delay } from "./helpers";
import * as db from "@/lib/mocks/db";
import * as store from "@/lib/mocks/api-keys";
import { request, NotSentError, mockRefusal } from "./http";
import { paths, type WireApiKey, type WireApiKeyMinted } from "./wire";

/**
 * API keys — FR-09, CMP-010, DAT-026.
 *
 * SOURCE-MIRRORED. Both halves exist now: `identity/apikey.go` resolves a
 * presented `hev_…` key by prefix and Argon2id hash, and `apikeyapi` serves
 * mint, list, rotate and revoke. This module was spec-derived and answered
 * `notImplemented` in http mode until FR-09 landed; the guard that said so
 * named its own remedy, and this is it.
 *
 * `isNotServedYet` stays exported below — `members` and `roles` still use it for
 * THEIR concepts, which are still unserved. It can no longer be true of this
 * module, and the hook no longer tests for it.
 */

export type ApiKeyState = "active" | "expired" | "revoked";

/**
 * A key as the console reads it. There is deliberately no `organisationId`:
 * every key the console can see is the signed-in organisation's — FR-09's org
 * scope is enforced by the list filter and the per-id lookups below, on the
 * wire row, before any mapping — and a mapped field nothing on the surface
 * reads would only be a value to be trusted later without anything testing it.
 */
export interface ApiKey {
  id: string;
  /** The non-secret lookup prefix — `hev_` plus eight characters. All a list ever shows. */
  prefix: string;
  expiresAt: string;
  revokedAt?: string;
  createdAt: string;
}

/** The ONE response that carries the key. Nothing else ever does. */
export interface MintedApiKey extends ApiKey {
  key: string;
}

/**
 * How long a new key lives — a CLOSED set.
 *
 * There is deliberately no "never". FR-09 describes the keys as "expiring" and
 * its acceptance condition is "expiry enforced": a key with no expiry has
 * nothing to enforce, and `WireApiKey.expires_at` is required for the same
 * reason. RAD §11's operations calendar has API keys reviewed and rotated
 * monthly, so 365 days is already generous — a key that must outlive a year is
 * rotated, not minted without end.
 */
export const EXPIRY_CHOICES = [30, 90, 365] as const;
export type ExpiryDays = (typeof EXPIRY_CHOICES)[number];

export function isExpiryChoice(days: unknown): days is ExpiryDays {
  return (EXPIRY_CHOICES as readonly unknown[]).includes(days);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** When a key minted at `now` for `days` expires. Pure — the clock comes in. */
export function expiresAtFor(days: ExpiryDays, now: Date): string {
  return new Date(now.getTime() + days * DAY_MS).toISOString();
}

/**
 * Where a key stands, derived the way `APIKeyResolver.Resolve` decides it:
 * a `revoked_at` refuses first, whatever the expiry; then `expires_at`
 * strictly before now is expired; anything else is live. A key expiring at
 * exactly `now` is still live — Go's `Before` is strict, and so is this.
 *
 * Pure. `now` is passed in, never read here, so a test can pin the clock and a
 * page can sample it once per render rather than once per row.
 */
export function keyState(key: Pick<ApiKey, "expiresAt" | "revokedAt">, now: Date): ApiKeyState {
  if (key.revokedAt) {
    instant(key.revokedAt, "revoked_at", "this key");
    return "revoked";
  }
  if (instant(key.expiresAt, "expires_at", "this key") < now.getTime()) return "expired";
  return "active";
}

type DateField = "expires_at" | "revoked_at" | "created_at";

/**
 * A timestamp the console will act on — or a refusal.
 *
 * `new Date("garbage").getTime()` is NaN, and every comparison with NaN is
 * false, so an unparseable `expires_at` would, unguarded, read as "not before
 * now" and the key as live, beside an "Invalid Date" in the Expires column. A
 * date the console cannot read is an expiry it cannot enforce (FR-09), so it
 * is refused rather than defaulted to the good state.
 *
 * `value` is `unknown` on purpose. `WireApiKey.expires_at` is typed required,
 * but RAD §8.2.18 leaves the column nullable and `APIKeyResolver.Resolve`
 * (identity/apikey.go) reads a NULL as never-expiring — a value the wire
 * could carry the day a management route lands. This console offers no
 * "never" (`EXPIRY_CHOICES`), so a null is refused HERE, by field name,
 * rather than crashing on `.slice`: the list fails either way, but a
 * TypeError would say nothing about which row or which field. Whether the
 * console should read a null the way `Resolve` does is a ruling for the spec,
 * not a default taken here.
 *
 * The wire value is quoted, bounded, in the error. That error fails the list
 * as a whole; the page then says only that the request did not complete
 * (`ListError` shows no message for a non-`ApiError`). The quoted value is
 * for whoever reads the failure, not for the screen.
 */
function instant(value: unknown, field: DateField, subject: string): number {
  if (typeof value !== "string") {
    throw new Error(`${subject}: ${field} is not a date (${JSON.stringify(String(value).slice(0, 40))})`);
  }
  const ms = new Date(value).getTime();
  const shown = value.slice(0, 40);
  if (Number.isNaN(ms)) {
    throw new Error(`${subject}: ${field} is not a date (${JSON.stringify(shown)})`);
  }
  return ms;
}

/**
 * Whether an error is this module saying "no route yet".
 *
 * The page branches on it: the honest state for http mode is "not served by
 * the platform yet", which is neither a crash nor an empty list.
 */
export function isNotServedYet(error: unknown): boolean {
  return error instanceof Error && /not implemented for HTTP mode/i.test(error.message);
}

/**
 * The boundary (invariant 9): a row whose dates cannot be parsed is refused
 * here, so the list fails as a whole rather than counting a key it cannot
 * place. `created_at` bears no state, but it is shown, and a page that
 * printed "Invalid Date" beside a prefix would be rendering the wire's
 * garbage as if it were a fact about the key.
 */
function fromWire(w: WireApiKey): ApiKey {
  const subject = `api key ${w.id}`;
  instant(w.expires_at, "expires_at", subject);
  if (w.revoked_at) instant(w.revoked_at, "revoked_at", subject);
  instant(w.created_at, "created_at", subject);
  return {
    id: w.id,
    prefix: w.prefix,
    expiresAt: w.expires_at,
    revokedAt: w.revoked_at || undefined,
    createdAt: w.created_at,
  };
}

function mintedFromWire(w: WireApiKeyMinted): MintedApiKey {
  return { ...fromWire(w), key: w.key };
}

/** The organisation the mock session belongs to — every key is scoped to it. */
function sessionOrgId(): string {
  return db.organisation.id;
}

/**
 * Lists this organisation's keys, revoked ones included.
 *
 * A revoked key stays listed: it is a record of a credential that existed and
 * when it stopped, not garbage. The filter on organisation is the mock's
 * version of FR-09's org scope — the real store keys management by `org_id`
 * explicitly (DAT-026).
 */
export async function listApiKeys(): Promise<ApiKey[]> {
  if (API_MODE === "http") {
    // The server orders newest-first and includes revoked rows; the boundary
    // below refuses a row whose dates it cannot read rather than placing it.
    return (await request<WireApiKey[]>(paths.apiKeys())).map(fromWire);
  }
  await delay(140);
  const org = sessionOrgId();
  return store.apiKeys.filter((k) => k.organisation_id === org).map(fromWire);
}

export interface MintInput {
  expiresInDays: ExpiryDays;
}

/**
 * Builds the row that is KEPT and the response that is SHOWN, as two objects,
 * so the store can never be handed the one with the key in it.
 */
function mintRow(expiresInDays: ExpiryDays, now: Date): { row: WireApiKey; shown: WireApiKeyMinted } {
  if (!isExpiryChoice(expiresInDays)) {
    throw new NotSentError(`a key expires in ${EXPIRY_CHOICES.join(", ")} days — not ${String(expiresInDays)}`);
  }
  const { key, prefix } = store.generateKey();
  const row: WireApiKey = {
    id: `ak-${now.getTime().toString(36)}-${prefix.slice(4)}`,
    organisation_id: sessionOrgId(),
    prefix,
    expires_at: expiresAtFor(expiresInDays, now),
    created_at: now.toISOString(),
  };
  return { row, shown: { ...row, key } };
}

/**
 * Mints a key scoped to the caller's organisation. The response is the only
 * time the key exists outside the caller's hands: the store keeps the prefix,
 * and the platform would keep an Argon2id hash (CMP-010 — "presented once at
 * creation").
 */
export async function mintApiKey(input: MintInput, now: Date = new Date()): Promise<MintedApiKey> {
  if (API_MODE === "http") {
    if (!isExpiryChoice(input.expiresInDays)) {
      throw new NotSentError(`a key expires in ${EXPIRY_CHOICES.join(", ")} days — not ${String(input.expiresInDays)}`);
    }
    return mintedFromWire(await request<WireApiKeyMinted>(paths.apiKeys(), {
      method: "POST",
      body: { expires_in_days: input.expiresInDays },
    }));
  }
  await delay(180);
  const { row, shown } = mintRow(input.expiresInDays, now);
  store.apiKeys.push(row);
  return mintedFromWire(shown);
}

export interface RotateInput {
  expiresInDays: ExpiryDays;
}

export interface Rotation {
  /** The replacement — carries the key, once. */
  minted: MintedApiKey;
  /**
   * The PREFIX of the key that was retired, supplied by the caller from the row
   * it asked to rotate.
   *
   * Not a whole `ApiKey`: the server's 201 carries the replacement and nothing
   * else, so a full retired row could only be assembled by guessing a
   * `revoked_at` the console never saw. The prefix is the one part of it the
   * caller already holds and the only part the confirmation shows.
   */
  revokedPrefix: string;
}

/**
 * Rotation — FR-09's "rotation supported". A replacement is minted under the
 * same name and the old key is revoked, as ONE step: in the mock the two
 * writes happen with no await between them, and every refusal (unknown id,
 * already revoked, an expiry outside the closed set) is raised before either.
 * A caller never observes a replacement without a revocation or the reverse.
 */
export async function rotateApiKey(
  id: string,
  input: RotateInput,
  now: Date = new Date(),
): Promise<MintedApiKey> {
  if (API_MODE === "http") {
    if (!isExpiryChoice(input.expiresInDays)) {
      throw new NotSentError(`a key expires in ${EXPIRY_CHOICES.join(", ")} days — not ${String(input.expiresInDays)}`);
    }
    // The server does both halves in one transaction, so there is no partial
    // state for this client to reconcile.
    return mintedFromWire(await request<WireApiKeyMinted>(paths.apiKeyRotate(id), {
      method: "POST",
      body: { expires_in_days: input.expiresInDays },
    }));
  }
  await delay(200);
  const old = store.apiKeys.find((k) => k.id === id && k.organisation_id === sessionOrgId());
  if (!old) throw mockRefusal("NOT_FOUND", `unknown api key: ${id}`);
  // 2.741: the platform answers a revoked key as NOT_FOUND (apikeys.Rotate
  // updates only an unrevoked row); the mock keeps the plainer sentence.
  if (old.revoked_at) throw mockRefusal("NOT_FOUND", "this key is already revoked — there is nothing to replace");
  // Validated before anything is written: a bad expiry must leave the old key
  // exactly as it was.
  const { row, shown } = mintRow(input.expiresInDays, now);
  old.revoked_at = now.toISOString();
  store.apiKeys.push(row);
  return mintedFromWire(shown);
}

/**
 * Revocation. Terminal — `Resolve` skips a revoked row before it even hashes
 * — and idempotent: a second revocation keeps the first instant, because the
 * key stopped working then and a later timestamp would misdate the event.
 * The row survives; it is listed as revoked.
 */
export async function revokeApiKey(id: string, now: Date = new Date()): Promise<void> {
  if (API_MODE === "http") {
    // 204, no body: the row is unchanged but for an instant the caller supplied
    // no part of, and the list already shows a revoked key as revoked.
    await request<void>(paths.apiKey(id), { method: "DELETE" });
    return;
  }
  await delay(160);
  const key = store.apiKeys.find((k) => k.id === id && k.organisation_id === sessionOrgId());
  if (!key) throw mockRefusal("NOT_FOUND", `unknown api key: ${id}`);
  key.revoked_at ??= now.toISOString();
}
